import { Link } from "react-router-dom";
import { usePlansCatalog, discountPct, inr } from "@/lib/plansCatalog";

export default function Pricing() {
  const { plans } = usePlansCatalog();
  return (
    <section id="pricing" className="py-20 md:py-28 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-14 md:mb-20">
          <span className="inline-block px-3 py-1 text-xs font-semibold text-primary-700 bg-primary-100 rounded-full mb-4">
            Pricing Plans
          </span>
          <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
            Choose the plan that fits your restaurant. All plans include a 7-day
            free trial with no credit card required.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`relative bg-background-50 border-2 ${plan.accent} rounded-lg p-6 md:p-8 flex flex-col ${
                plan.popular ? "shadow-lg shadow-primary-500/10 scale-[1.02]" : ""
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 bg-accent-500 text-background-50 text-xs font-semibold rounded-full whitespace-nowrap">
                  Most Popular
                </div>
              )}

              <div className="mb-6">
                <h3 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-1">
                  {plan.name}
                </h3>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                  {plan.description}
                </p>
              </div>

              <div className="mb-6">
                <span className="text-3xl md:text-4xl font-heading font-extrabold text-foreground-950">
                  {plan.price}
                </span>
                {plan.period && (
                  <span className="text-sm text-foreground-500 ml-1">{plan.period}</span>
                )}
                {discountPct(plan) > 0 && (
                  <span className="ml-2 text-base text-foreground-400 line-through">{inr(plan.mrp!)}</span>
                )}
                <p className="text-xs text-foreground-500 mt-1">per outlet</p>
                <div className="mt-2 flex items-center gap-2">
                  <div className="text-xs font-medium text-primary-700 bg-primary-100 inline-block px-2.5 py-1 rounded-full">
                    7-Day Free Trial
                  </div>
                  {discountPct(plan) > 0 && (
                    <span className="text-xs font-bold text-rose-600 bg-rose-50 px-2 py-1 rounded-full">
                      {discountPct(plan)}% OFF
                    </span>
                  )}
                </div>
              </div>

              <ul className="space-y-3 mb-8 flex-1">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2.5 text-sm text-foreground-700">
                    <span className="w-5 h-5 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-xs"></i>
                    </span>
                    {feature}
                  </li>
                ))}
              </ul>

              <button
                className={`w-full py-3 text-sm font-semibold rounded-md transition-all whitespace-nowrap cursor-pointer ${
                  plan.popular
                    ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                    : "bg-background-100 text-foreground-900 hover:bg-background-200 border border-background-300"
                }`}
              >
                {plan.cta}
              </button>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-primary-700 bg-primary-100 hover:bg-primary-200 rounded-md transition-colors cursor-pointer"
          >
            <i className="ri-table-line"></i>
            Plan comparison at a glance
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
